import { HttpStatusCode } from "axios"
import DOMPurify from "dompurify"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import EventPhoto from "../../../App/assets/EventPhoto.jpg"
import { ResultType, RoleType, SearchFilter } from "../../../App/enums"
import { organizationProfileDefault, profileDefault } from "../../../App/GlobaleVariables"
import { formatDate } from "../../../App/Helpers/Helpers"
import { selectTranslations } from "../../../App/i18n/i18nSlice"
import { htmlRegex } from "../../../App/Regex"
import { RootState } from "../../../App/store"
import { GlobalSearchResult } from "../../../Types/GlobalSearchResultType"
import { State } from "../../../Types/State"
import { StateGetRequest } from "../../Authentication/SignUp/SignupServiceRequests"
import Image from "../../Shared/Primitive/Image"
import { LoadingAnimation, LocationIconGreen } from "../Components/SvgIcons"
import { onGetGlobalSearchFilteredData } from "../Search/GlobalSearchServiceRequest"
import { onSearchResultClick } from "../Search/onSearchResultClick"
import { onNameFilter, onPhotoFilter } from "../Search/SearchDataFilterFunctions"
import SearchFilterContainer from "../Search/SearchFilterContainer"
import { setHashtagData } from "./LocationSlice"
import { onHashtagClick } from "./onHashtagClick"

function LocationCard() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { state_id } = useParams()
    const [isLoading, setIsLoading] = useState(true)
    const [keyword,setKeyword] = useState("")
    const [currentState,setCurrentState] = useState<State>()
    const [pageNum, setPageNum] = useState(1)
    const [hasNextPage, setHasNextPage] = useState(false)
    const [isLoadingMoreData, setIsLoadingMoreData] = useState(false)
    const [filterState, setFilterState] = useState<string>(SearchFilter.all_results)
    const [showPost, setShowPost] = useState<{ [key: number]: boolean }>({})
    const scrollContainer = useRef<HTMLDivElement>(null)
    const content = useSelector((state: RootState) => state.hashtagSlice.hashtagSlice.result)
    const role = useSelector((state: RootState) => state.rolesSlice.RolesSlice.roles)
    const userDataState = useSelector((state: RootState) => state.userDataSlice)
    const translation = useSelector(selectTranslations)
    const maxPostLength = 100

    useEffect(() => {
        const getData = setTimeout(() => {
            const controller = new AbortController()
            const { signal } = controller
            if (state_id) {
                setIsLoading(true)
                onGetGlobalSearchFilteredData(state_id, 1, filterState, false,true)
                    .then(response => {
                        if (response.status === 200) {
                            dispatch(setHashtagData({ result: response.data.results }))
                            setHasNextPage(Boolean(response.data.next!)!)
                            setIsLoading(false)
                            setPageNum(1)
                        }
                    })
                    .catch(error => {
                        setPageNum(1)
                        if (signal.aborted) return
                        console.log(error)
                    })
            }
          }, 2000)
      
          return () => clearTimeout(getData)
    }, [dispatch, filterState, state_id])

    useEffect(() => {
        setIsLoading(true)
    },[filterState])
    
    useEffect(() => {
        if(state_id){
            let id = parseInt(state_id)
            StateGetRequest(id).then(response => {
                if(response.status === HttpStatusCode.Ok){
                    setCurrentState(response.data)
                }
            })
        }   
    },[dispatch, state_id])

    const editPostContent = (content: string, result: GlobalSearchResult) => {
        const cleanHtml = DOMPurify.sanitize(content, {
            ALLOWED_TAGS: ["b", "p", "span", "a", "div", "img", "header", "footer","br"],
            ALLOWED_ATTR: ["class", "href", "target", "data-type", "className", "id", "rel", "src", "style", "width", "type", "data-reactroot"],
        })

        let newString = cleanHtml.replaceAll(htmlRegex, "").replaceAll("&lt;", "<").replaceAll("&gt;", ">").replaceAll("&nbsp;", " ")

        const onButtonClick = (index: number) => () => {
            setShowPost(state => ({
                ...state,
                [index]: !state[index],
            }))
        }

        if (newString.length > maxPostLength) {
            let newHTML = !showPost[result?.id!] ? newString.substring(0, maxPostLength) + "..." : newString
            const parts = newHTML.split(new RegExp(`(${keyword})`, "gi"))

            return (
                <div>
                    <div className={`search-component__text search-component__text--post`}>
                        {parts.map((part, i) => (
                            <span
                                key={i}
                                className={`search-component__text search-component__text--font-size-14 ${
                                    part.toLowerCase() === keyword!.toLowerCase() && "search-component__text--bold"
                                }`}
                                onClick={() => onSearchResultClick(result, navigate, dispatch, role, userDataState.id, parseInt(state_id!))}
                            >
                                {part}
                            </span>
                        ))}
                    </div>
                    <div className="search-component__post-button" onClick={onButtonClick(result?.id!)} key={result?.id!}>
                        {!showPost[result?.id!] ? translation.seeAllDetails : translation.showLess}
                    </div>
                </div>
            )
        }

        const parts = newString.split(new RegExp(`(${keyword})`, "gi"))

        return (
            <span className={`search-component__text search-component__text--post`}>
                {parts.map((part, i) => (
                    <span
                        key={i}
                        className={`search-component__text search-component__text--font-size-14 ${
                            part.toLowerCase() === keyword!.toLowerCase() && "search-component__text--bold"
                        }`}
                        onClick={() => onSearchResultClick(result, navigate, dispatch, role, userDataState.id, parseInt(state_id!))}
                    >
                        {part}
                    </span>
                ))}
            </span>
        )
    }

    const onScroll = () => {
        if (scrollContainer.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current

            if (Math.ceil(scrollTop + clientHeight + 1) >= scrollHeight && hasNextPage && !isLoadingMoreData) {
                const controller = new AbortController()
                const { signal } = controller
                setIsLoadingMoreData(true)

                onGetGlobalSearchFilteredData(keyword!, pageNum + 1, filterState, true)
                    .then(response => {
                        if (response.status === 200) {
                            dispatch(setHashtagData({ result: [...content, ...response.data.results] }))
                            setHasNextPage(Boolean(response.data.next!)!)
                            setPageNum(pageNum + 1)
                            setIsLoadingMoreData(false)
                        }
                    })
                    .catch(error => {
                        setPageNum(1)
                        if (signal.aborted) return
                        console.log(error)
                    })
            }
        }
    }

    return (
        <div className="location-component">
            <div className="location-component__content">
                <div className="location-component__header">
                    <div className="location-component__header-image">
                        <LocationIconGreen/>
                    </div>
                    <div className="location-component__header-title-container">
                        <p className="location-component__header-title">{state_id}</p>
                        <p className="location-component__header-subtext">
                            {content && (content!.length === 0 || content?.length > 1)
                                ? content?.length + " " + translation.results
                                : content?.length + " " + translation.result}
                        </p>
                    </div>
                </div>
                <SearchFilterContainer filterState={filterState} setFilterState={setFilterState} setPageNum={setPageNum} isGlobaleSearch={false} isLocationSearch={true} />
                {isLoading ? (
                    <div className="location-component__results-container">
                        <LoadingAnimation type="cover" />
                    </div>
                ) : (
                    <div className="location-component__results-container">
                        <div className="location-component__results-content" ref={scrollContainer} onScroll={onScroll}>
                            {content &&
                                content!.length > 0 &&
                                content!.map((result, index) => {
                                    if (result.type === ResultType.Post || result.type === ResultType.Comment) {
                                        return (
                                            <div
                                                className={`location-component__result-container ${
                                                    content.length - 1 === index && "location-component__result-container--no-border"
                                                } `}
                                                key={result.id + result.type}
                                            >
                                                <div
                                                    className={`location-component__result location-component__result--no-border
                                            location-component__result--post `}
                                                    onClick={() => onSearchResultClick(result, navigate, dispatch, role, userDataState.id, parseInt(state_id!))}
                                                >
                                                    <div className="location-component__left-panel">
                                                        <img
                                                            className="location-component__image"
                                                            src={
                                                                result.content_type === RoleType.ORGANIZATION.toLowerCase()
                                                                    ? result.content_object?.photo?.url_path || organizationProfileDefault
                                                                    : result.content_object?.profile_photo?.url_path || profileDefault
                                                            }
                                                            alt=""
                                                        />
                                                    </div>

                                                    <div className={`location-component__middle-panel`}>
                                                        <div
                                                            className={`location-component__text location-component__text--font-size-14  location-component__text--bold ${
                                                                !result.content_object?.id && "location-component__text--deleted"
                                                            }`}
                                                        >
                                                            {result.content_type === RoleType.ORGANIZATION.toLowerCase()
                                                                ? result.content_object?.name || translation.deletedOrganization
                                                                : result.content_object === null
                                                                ? translation.deletedUser
                                                                : result.content_object?.first_name + " " + result.content_object?.last_name}
                                                        </div>
                                                        <div className={`location-component__text location-component__text--font-size-12`}>
                                                            {translation.at + " " + result.room_name!}
                                                        </div>
                                                        <div className="location-component__subtext location-component__subtext--post">
                                                            {result.type === ResultType.Post ? translation.post : translation.comment}
                                                        </div>

                                                        {result.type === ResultType.Post && result.post_file && (
                                                            <Image className="location-component__post-image" src={result.post_file} alt="" />
                                                        )}
                                                    </div>

                                                    <div className={`location-component__right-panel-post`}>{formatDate(result.date!, "HH:mm dd MMM")!}</div>
                                                </div>
                                                {editPostContent(result.content!, result)}
                                            </div>
                                        )
                                    }

                                    return (
                                        <div
                                            key={result.id + result.type}
                                            className={`location-component__result ${content.length - 1 === index && "location-component__result--no-border"}`}
                                        >
                                            <div
                                                className="location-component__left-panel"
                                                onClick={() => onSearchResultClick(result, navigate, dispatch, role, userDataState.id, parseInt(state_id!))}
                                            >
                                                {result.type === ResultType.Event ? (
                                                    <Image className="location-component__image" src={onPhotoFilter(result) || EventPhoto} alt="" />
                                                ) : null}
                                                {result.type !== ResultType.Event && (
                                                    <Image className="location-component__image" src={onPhotoFilter(result)} alt="" />
                                                )}
                                            </div>
                                            <div className={`location-component__right-panel`}>
                                                <div
                                                    className={`location-component__text ${
                                                        result.type === ResultType.Event && "location-component__text--font-large"
                                                    }`}
                                                    onClick={() => onSearchResultClick(result, navigate, dispatch, role, userDataState.id, parseInt(state_id!))}
                                                >
                                                    {onNameFilter(result!)}
                                                </div>

                                                <div
                                                    className="location-component__subtext"
                                                    onClick={() => onSearchResultClick(result, navigate, dispatch, role, userDataState.id, parseInt(state_id!))}
                                                >
                                                    {result.type === ResultType.User
                                                        ? translation.individual
                                                        : result.type === ResultType.Organization
                                                        ? translation.organization
                                                        : result.type === ResultType.Room
                                                        ? translation.room
                                                        : result.type === ResultType.Hashtag
                                                        ? result.total_results === 1
                                                            ? result.total_results + " " + translation.result
                                                            : result.total_results + " " + translation.results
                                                        : translation.event}
                                                </div>
                                                {(result.type === ResultType.User || ResultType.Organization) && (
                                                    <div className="location-component__tag-container">
                                                        {result.hashtags?.map((hashtag, index) => (
                                                            <div
                                                                className="location-component__tag"
                                                                key={index}
                                                                onClick={() => onHashtagClick(hashtag, navigate, role, parseInt(state_id!))}
                                                            >
                                                                {hashtag.charAt(0) !== "#" ? "#" + hashtag : hashtag}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            {isLoadingMoreData && (
                                <div className="rooms-card__container">
                                    <LoadingAnimation type="profile" />
                                </div>
                            )}
                            {content === undefined && (
                                <div className="search-dropdown__no-data-text">{`${translation.noResultThatContain} '${keyword}' ${translation.wasFound}`}</div>
                            )}
                            {content && content!.length === 0 && (keyword!.length !== 0 || keyword!.length === 0) && (
                                <div className="search-component__no-data-text">{translation.nothingToShowYet}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default LocationCard
